/**
 * Step4QualityChecklist — final QC gate before SM Review.
 *
 * Mechanic picks a checklist template (Standard Service / Major Service /
 * Repair / RWC …, defined by Super Admin under Templates), ticks every
 * item, and sets the Next Service Date. Items render dynamically from the
 * selected template so a quick repair JC doesn't need to drag a
 * 30-point major-service checklist.
 *
 * iPad-friendly: 44px+ touch targets, full-row tappable labels.
 */
import { useEffect, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, CheckCircle2, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback used when no templates exist yet — matches the legacy 6-item set.
const LEGACY_ITEMS = [
  { key: 'repairs_checked', label: 'Repairs checked' },
  { key: 'brakes_checked', label: 'Brakes checked' },
  { key: 'tyres_checked', label: 'Tyres checked' },
  { key: 'wheel_alignment_checked', label: 'Wheel alignment checked' },
  { key: 'fluids_topped', label: 'Fluids topped' },
  { key: 'engine_oil_checked', label: 'Engine oil checked' },
];

export default function Step4QualityChecklist({
  jobCard, checklist, checklistComplete, canEdit,
  nextServiceDate, handleChecklistChange, handleNextServiceDateChange, onUpdate,
}) {
  const [templates, setTemplates] = useState([]);
  const [selectedTplId, setSelectedTplId] = useState(jobCard.checklist_template_id || '');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/template-library?template_type=service_checklist`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!active) return;
        setTemplates(data.templates || []);
        // Auto-select the JC's already-saved template or the default one
        if (!jobCard.checklist_template_id) {
          const def = (data.templates || []).find(t => t.content?.default);
          if (def) setSelectedTplId(def.template_id);
        }
      } catch (_e) { /* noop */ }
    })();
    return () => { active = false; };
  }, [jobCard.checklist_template_id]);

  const currentTpl = templates.find(t => t.template_id === selectedTplId);
  const items = currentTpl?.content?.items?.length ? currentTpl.content.items : LEGACY_ITEMS;

  const handleTplChange = async (newId) => {
    setSelectedTplId(newId);
    // Persist on the JC so the same checklist re-renders for the SM
    try {
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checklist_template_id: newId || null }),
      });
      if (res.ok) {
        onUpdate && onUpdate();
      } else {
        toast.error('Could not update checklist template');
      }
    } catch (_e) { toast.error('Network error updating template'); }
  };

  return (
    <section
      className={`border rounded-md ${checklistComplete ? 'border-green-300' : 'border-purple-200'}`}
      data-testid="jc-step-4-qc"
    >
      <header className={`flex items-center gap-2 px-3 py-2 border-b rounded-t-md ${checklistComplete ? 'bg-green-50 border-green-200' : 'bg-purple-50 border-purple-200'}`}>
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold ${checklistComplete ? 'bg-green-600' : 'bg-purple-600'}`}>4</span>
        <ClipboardCheck className={`h-4 w-4 ${checklistComplete ? 'text-green-700' : 'text-purple-700'}`} />
        <h4 className={`text-sm font-semibold ${checklistComplete ? 'text-green-900' : 'text-purple-900'}`}>Quality Checklist</h4>
        {checklistComplete ? (
          <span className="text-[11px] text-green-700 ml-auto flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Ready for SM Review
          </span>
        ) : (
          <span className="text-[11px] text-purple-700/80 ml-auto">Tick all items + set next service date to unlock SM Review</span>
        )}
      </header>
      <div className="p-3 space-y-3">
        {templates.length > 0 && (
          <div className="max-w-sm">
            <Label className="text-xs">Checklist Template</Label>
            <Select value={selectedTplId} onValueChange={handleTplChange} disabled={!canEdit}>
              <SelectTrigger className="mt-1 h-11 text-base touch-manipulation" data-testid="qc-template-select">
                <SelectValue placeholder="Pick a service-specific checklist…" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.template_id} value={t.template_id}>
                    {t.name}
                    {t.content?.default ? ' (default)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">
              Different services need different checks — Super Admin creates templates under Templates → Service Checklists.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          {items.map((item) => (
            <label
              key={item.key}
              htmlFor={item.key}
              className="flex items-center gap-3 min-h-11 px-2 -mx-2 rounded hover:bg-slate-50 cursor-pointer touch-manipulation"
            >
              <Checkbox
                id={item.key}
                checked={checklist[item.key] || false}
                onCheckedChange={(checked) => handleChecklistChange(item.key, checked)}
                disabled={!canEdit}
                className="h-5 w-5"
                data-testid={`qc-${item.key}`}
              />
              <span className={`text-base ${checklist[item.key] ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                {item.label}
              </span>
            </label>
          ))}
        </div>
        {/* iter138.6 — Duplicate "Next Service Date" input removed.
            It lives now ONLY in the Vehicle Details section above
            (Step 1) and writes through to quality_checklist as well
            for backward compat. Keep this hint so the SM knows where
            to set it if they missed it. */}
        {(!nextServiceDate || !nextServiceDate.trim()) && (
          <div className="pt-3 border-t border-slate-100 max-w-md">
            <p className="text-xs text-amber-700 flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              Next Service Date is missing —
              <span className="font-medium">set it in the Vehicle Details section above</span>
              <span className="text-red-500">*</span>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
